// ============================================================
//  src/App.jsx
//  Root component — React Router v6, global state, auth session
// ============================================================

import { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";


import Navbar            from "./components/Navbar";
import Footer            from "./components/Footer";
import HomePage          from "./pages/HomePage";
import SignInPage        from "./pages/SignInPage";
import CartPage          from "./pages/CartPage";
import ContactPage       from "./pages/ContactPage";
import RefundPolicy      from "./pages/RefundPolicy";
import ProfilePage       from "./pages/ProfilePage";
import OrdersPage        from "./pages/OrdersPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import AddressPage       from "./pages/AddressPage";
import AdminPanel        from "./pages/AdminPanel";
import SubscriptionPage  from "./pages/SubscriptionPage";
import CategoryPage      from "./pages/CategoryPage";

import useAuth from "./hooks/useAuth";
import useCart from "./hooks/useCart";
import { Toast } from "./components/UI";
import SEO      from "./hooks/useSEO";

import "./styles/global.css";

// ── Inject Google Fonts + Material Icons once for ALL routes ─
// Call this in App so every page gets fonts regardless of which
// URL the user lands on or refreshes.
function useGlobalAssets() {
  useEffect(() => {
    const links = [
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700;9..40,800&family=Playfair+Display:wght@700;800&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/icon?family=Material+Icons+Round",
      },
    ];

    links.forEach(({ rel, href, crossOrigin }) => {
      if (document.querySelector(`link[href="${href}"]`)) return; // skip if already injected
      const el = document.createElement("link");
      el.rel  = rel;
      el.href = href;
      if (crossOrigin) el.crossOrigin = crossOrigin;
      document.head.appendChild(el);
    });
    // Note: no cleanup — we want these to persist for the app's lifetime
  }, []);
}

// ── Category slugs ─────────────────────────────────────────
export const CATEGORIES = {
  millets:               "Millets",
  "traditional-rice":    "Traditional Rice",
  "dry-fruits-nuts":     "Dry Fruits & Nuts",
  sweeteners:            "Sweeteners",
};

// ── Protected Route wrapper ─────────────────────────────────
function ProtectedRoute({ auth }) {
  if (auth.loading) return null;
  return auth.user ? <Outlet /> : <Navigate to="/signin" replace />;
}

// ── Layout wrapper (Navbar + Footer + Toast) ────────────────
function Layout({ auth, cart, setCart, globalToast, setGlobalToast, orderInProgress, setOrderInProgress }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = location.pathname === "/admin";

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: "#faf8f5", minHeight: "100vh" }}>
      {!isAdmin && (
        <Navbar
          cart={cart}
          auth={auth}
          setGlobalToast={setGlobalToast}
          orderInProgress={orderInProgress}
        />
      )}

      <main>
        <Outlet />
      </main>

      <Footer />

      {globalToast && (
        <Toast message={globalToast} onDone={() => setGlobalToast(null)} />
      )}
    </div>
  );
}

// ── Root App ────────────────────────────────────────────────
export default function App() {
  const [cart, setCart]                       = useCart([]);
  const [globalToast, setGlobalToast]         = useState(null);
  const [orderInProgress, setOrderInProgress] = useState(false);

  const auth = useAuth();

  // ✅ Inject Google Fonts + Material Icons for ALL routes/pages
  useGlobalAssets();

  // Safety net: reset order state on logout
  useEffect(() => {
    if (!auth.user) {
      setOrderInProgress(false);
    }
  }, [auth.user]);

  if (auth.loading) {
    return (
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        minHeight: "100vh", background: "#faf8f5", gap: 14,
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Material icon instead of emoji */}
        <span className="material-icons-round" style={{ fontSize: 48, color: "#4a7c59" }}>
          grass
        </span>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#555", letterSpacing: 0.3 }}>
          Loading RK Farm Foods...
        </div>
      </div>
    );
  }

  const sharedProps = {
    auth, cart, setCart,
    globalToast, setGlobalToast,
    orderInProgress, setOrderInProgress,
  };

  return (
    <Routes>
      <Route element={<Layout {...sharedProps} />}>

        {/* ── Public routes ── */}
        <Route
          path="/"
          element={
            <>
              <SEO page="home" />
              <HomePage cart={cart} setCart={setCart} auth={auth} />
            </>
          }
        />

        <Route
          path="/product/:productId"
          element={
            <>
              <SEO page="product" />
              <ProductDetailPage setCart={setCart} />
            </>
          }
        />

        <Route
          path="/signin"
          element={
            <>
              <SEO page="signin" />
              <SignInPage auth={auth} />
            </>
          }
        />

        <Route
          path="/cart"
          element={
            <>
              <SEO page="cart" />
              <CartPage
                cart={cart}
                setCart={setCart}
                auth={auth}
                onPlacingChange={setOrderInProgress}
              />
            </>
          }
        />

        <Route
          path="/contact"
          element={
            <>
              <SEO page="contact" />
              <ContactPage />
            </>
          }
        />

        <Route
          path="/refund"
          element={
            <>
              <SEO page="refund" />
              <RefundPolicy />
            </>
          }
        />

        <Route
          path="/subscription"
          element={
            <>
              <SEO page="subscription" />
              <SubscriptionPage auth={auth} />
            </>
          }
        />

        <Route
          path="/category/:slug"
          element={
            <CategoryPage
              cart={cart}
              setCart={setCart}
              categories={CATEGORIES}
            />
          }
        />

        <Route path="/admin" element={<AdminPanel />} />

        {/* ── Protected routes ── */}
        <Route element={<ProtectedRoute auth={auth} />}>
          <Route
            path="/profile"
            element={
              <>
                <SEO page="profile" />
                <ProfilePage auth={auth} setGlobalToast={setGlobalToast} />
              </>
            }
          />

          <Route
            path="/orders"
            element={
              <>
                <SEO page="orders" />
                <OrdersPage auth={auth} />
              </>
            }
          />

          <Route
            path="/address"
            element={
              <>
                <SEO page="address" />
                <AddressPage auth={auth} />
              </>
            }
          />
        </Route>

        {/* ── 404 fallback ── */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Route>
    </Routes>
  );
}
